import React, { useState, useEffect, useMemo } from "react";
import { cn } from "../../../lib/utils";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { Button } from "../../../ui/button";
import GatewayMultiSelect from "./GatewayMultiSelect";
import { DateTimePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { TextField, ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material";
import "../AgenticGateway.css";

function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

type FilterOption = string | { value: string; label: string };

interface FilterField {
  key: string;
  label: string;
  type: "select" | "multiselect" | "text" | "date" | "ip";
  options?: FilterOption[];
  placeholder?: string;
}

type FilterValue = string | string[];

interface GatewayFilterBarProps {
  fields: FilterField[];
  values: Record<string, FilterValue>;
  onChange: (key: string, value: FilterValue) => void;
  onApply: () => void;
  onClear: () => void;
  className?: string;
}

function isFilled(v: FilterValue | undefined | null): boolean {
  if (v === undefined || v === null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export default function GatewayFilterBar({ fields, values, onChange, onApply, onClear, className }: GatewayFilterBarProps) {
  const [expanded, setExpanded] = useState(false);
  const activeCount = Object.values(values).filter(isFilled).length;
  const isDark = useIsDark();
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: isDark ? "#3b82f6" : "#2563eb" },
          background: {
            paper: isDark ? "#1e2533" : "#ffffff",
            default: isDark ? "#1a1f2b" : "#f4f6f9",
          },
        },
      }),
    [isDark]
  );

  return (
    <div className={cn(
      "rounded-2xl border bg-card overflow-hidden transition-colors",
      expanded || activeCount > 0 ? "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.15)]" : "border-transparent portal-card-shadow",
      className
    )}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm text-foreground hover:bg-primary/5 transition-colors group"
      >
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-7 h-7 rounded-lg flex items-center justify-center transition-colors",
            expanded || activeCount > 0 ? "bg-primary/15 text-primary" : "ag-surface text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
          )}>
            <Search className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold">Filters</span>
          {activeCount > 0 ? (
            <span className="px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-primary text-[10px] font-bold uppercase tracking-wider">
              {activeCount} active
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground/70 ml-1">Click to refine results</span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-primary" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-3">
            {fields.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{field.label}</label>
                {field.type === "select" ? (
                  <select
                    value={(values[field.key] as string) || ""}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    className="w-full bg-card border border-border rounded-lg px-3 py-2 text-xs shadow-[0_1px_3px_rgb(15_23_42/0.04)] text-foreground/80 outline-none focus:border-primary/50"
                  >
                    <option value="">All</option>
                    {field.options?.map((opt) => {
                      const v = typeof opt === "string" ? opt : opt.value;
                      const l = typeof opt === "string" ? opt : opt.label;
                      return <option key={v} value={v}>{l}</option>;
                    })}
                  </select>
                ) : field.type === "multiselect" ? (
                  <GatewayMultiSelect
                    options={(field.options || []).map((o) => (typeof o === "string" ? o : o.value))}
                    values={Array.isArray(values[field.key]) ? (values[field.key] as string[]) : []}
                    onChange={(vals) => onChange(field.key, vals)}
                    placeholder="All"
                  />
                ) : field.type === "ip" ? (
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={(values[field.key] as string) || ""}
                      onChange={(e) => onChange(field.key, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") onApply(); }}
                      placeholder={field.placeholder || "e.g. 192.168.1.* or 10.0.0.5"}
                      className="flex-1 min-w-0 bg-card border border-border rounded-lg px-3 py-2 text-xs shadow-[0_1px_3px_rgb(15_23_42/0.04)] text-foreground/80 placeholder-muted-foreground outline-none focus:border-primary/50 font-mono"
                    />
                    <Button
                      type="button"
                      onClick={onApply}
                      size="sm"
                      variant="outline"
                      className="h-auto px-2 py-1 border-primary/40 text-primary hover:bg-primary/10 text-xs"
                    >
                      <Search className="w-3 h-3" />
                    </Button>
                  </div>
                ) : field.type === "date" ? (
                  <MuiThemeProvider theme={muiTheme}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      value={values[field.key] && typeof values[field.key] === "string" ? new Date(values[field.key] as string) : null}
                      onChange={(d: Date | null) => onChange(field.key, d ? d.toISOString() : "")}
                      maxDateTime={new Date()}
                      ampm={false}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          size="small"
                          variant="outlined"
                          fullWidth
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "Select date & time",
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              fontSize: "0.75rem",
                              backgroundColor: "hsl(var(--card))",
                              color: "hsl(var(--foreground))",
                              borderRadius: "0.375rem",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "hsl(var(--border))",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 10px",
                            },
                            "& .MuiSvgIcon-root": {
                              color: "hsl(var(--muted-foreground))",
                              fontSize: "1rem",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "hsl(var(--primary) / 0.5)",
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "hsl(var(--primary)) !important",
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  </MuiThemeProvider>
                ) : (
                  <input
                    type="text"
                    value={(values[field.key] as string) || ""}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    placeholder={field.placeholder || ""}
                    className="w-full bg-card border border-border rounded-lg px-3 py-2 text-xs shadow-[0_1px_3px_rgb(15_23_42/0.04)] text-foreground/80 placeholder-muted-foreground outline-none focus:border-primary/50"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button onClick={onApply} size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)] text-xs">
              Apply Filters
            </Button>
            <Button onClick={onClear} variant="outline" size="sm" className="text-xs">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
